import React, { useState, useMemo } from 'react';
import {
  Search,
  X,
  ChevronUp,
  ChevronDown,
  Eye,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { parseAsLocalDate } from '@/utils/dateHelpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FinalizedPayoutsTableProps {
  payouts: any[];
  loading: boolean;
  onViewPayout: (payout: any) => void;
}

type SortDirection = 'asc' | 'desc' | null;

type SortField =
  | 'loan_number'
  | 'borrower'
  | 'loan_amount'
  | 'gross_check'
  | 'lo_payout'
  | 'fund_date'
  | 'finalized_at'
  | 'status';

type TimePeriod =
  | 'all'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year';

interface ColumnDef {
  key: SortField;
  label: string;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
}

const COLUMNS: ColumnDef[] = [
  { key: 'loan_number', label: 'Loan #', sortable: true },
  { key: 'borrower', label: 'Borrower', sortable: true },
  { key: 'loan_amount', label: 'Loan Amount', sortable: true, align: 'right' },
  { key: 'gross_check', label: 'Gross Check', sortable: true, align: 'right' },
  { key: 'lo_payout', label: 'LO Payout', sortable: true, align: 'right' },
  { key: 'fund_date', label: 'Funded', sortable: true },
  { key: 'finalized_at', label: 'Finalized', sortable: true },
  { key: 'status', label: 'Status', sortable: true, align: 'center' }
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getFinalizedDate(payout: any): string | null {
  return payout.finalized_at || payout.updated_at || null;
}

function getTimePeriodBounds(period: TimePeriod): { start: Date; end: Date } | null {
  if (period === 'all') {
return null;
}

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarter = Math.floor(month / 3);

  switch (period) {
    case 'this_month':
      return {
        start: new Date(year, month, 1),
        end: new Date(year, month + 1, 0, 23, 59, 59, 999)
      };
    case 'last_month': {
      const lm = month === 0 ? 11 : month - 1;
      const ly = month === 0 ? year - 1 : year;
      return {
        start: new Date(ly, lm, 1),
        end: new Date(ly, lm + 1, 0, 23, 59, 59, 999)
      };
    }
    case 'this_quarter':
      return {
        start: new Date(year, quarter * 3, 1),
        end: new Date(year, quarter * 3 + 3, 0, 23, 59, 59, 999)
      };
    case 'last_quarter': {
      const lq = quarter === 0 ? 3 : quarter - 1;
      const lqy = quarter === 0 ? year - 1 : year;
      return {
        start: new Date(lqy, lq * 3, 1),
        end: new Date(lqy, lq * 3 + 3, 0, 23, 59, 59, 999)
      };
    }
    case 'this_year':
      return {
        start: new Date(year, 0, 1),
        end: new Date(year, 11, 31, 23, 59, 59, 999)
      };
    case 'last_year':
      return {
        start: new Date(year - 1, 0, 1),
        end: new Date(year - 1, 11, 31, 23, 59, 59, 999)
      };
    default:
      return null;
  }
}

function getSortValue(payout: any, field: SortField): string | number | null {
  switch (field) {
    case 'loan_number':
      return (payout.loan_number || '').toLowerCase();
    case 'borrower':
      return (payout.borrower_name || '').toLowerCase();
    case 'loan_amount':
      return Number(payout.loan_amount) || 0;
    case 'gross_check':
      return Number(payout.gross_check_received) || 0;
    case 'lo_payout':
      return Number(payout.gross_amount) || 0;
    case 'fund_date':
      return payout.fund_date
        ? parseAsLocalDate(payout.fund_date).getTime()
        : null;
    case 'finalized_at': {
      const d = getFinalizedDate(payout);
      return d ? new Date(d).getTime() : null;
    }
    case 'status':
      return payout.status || '';
    default:
      return null;
  }
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount == null || isNaN(Number(amount))) {
return '-';
}
  return `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FinalizedPayoutsTable({ payouts, loading, onViewPayout }: FinalizedPayoutsTableProps) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');

  // 3-click sort cycle: asc -> desc -> clear
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredPayouts = useMemo(() => {
    let result = payouts;

    // Search filter
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((p) => {
        const loanMatch = (p.loan_number || '').toLowerCase().includes(q);
        const borrowerMatch = (p.borrower_name || '').toLowerCase().includes(q);
        return loanMatch || borrowerMatch;
      });
    }

    // Time period filter (based on finalized_at ?? updated_at)
    const bounds = getTimePeriodBounds(timePeriod);
    if (bounds) {
      result = result.filter((p) => {
        const dateStr = getFinalizedDate(p);
        if (!dateStr) {
return false;
}
        const d = new Date(dateStr);
        return d >= bounds.start && d <= bounds.end;
      });
    }

    // Sort
    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        const aVal = getSortValue(a, sortField);
        const bVal = getSortValue(b, sortField);

        if (aVal === bVal) {
return 0;
}
        if (aVal === null) {
return 1;
}
        if (bVal === null) {
return -1;
}

        const comparison = aVal < bVal ? -1 : 1;
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [payouts, search, timePeriod, sortField, sortDirection]);

  const hasActiveFilters = search.trim() !== '' || timePeriod !== 'all';
  const showingSubset = filteredPayouts.length !== payouts.length;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading finalized records...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        {/* Search + Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search loan #, borrower..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-background"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Time Period */}
          <Select value={timePeriod} onValueChange={(v) => setTimePeriod(v as TimePeriod)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Time Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="last_quarter">Last Quarter</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
              <SelectItem value="last_year">Last Year</SelectItem>
            </SelectContent>
          </Select>

          {/* Record count */}
          {showingSubset && (
            <span className="text-xs text-muted-foreground">
              Showing {filteredPayouts.length} of {payouts.length}
            </span>
          )}
        </div>

        {/* Table */}
        {payouts.length === 0 && !hasActiveFilters ? (
          <div className="text-center py-12 text-muted-foreground">
            No finalized reconciliations found.
          </div>
        ) : filteredPayouts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No matching records found. Try adjusting your search or filters.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableHead
                    key={col.key}
                    onClick={col.sortable ? () => handleSort(col.key) : undefined}
                    className={cn(
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      col.sortable && 'cursor-pointer hover:bg-muted/70 select-none transition-colors',
                      sortField === col.key && 'bg-primary/10 text-primary'
                    )}
                  >
                    <span className={cn(
                      'flex items-center gap-1 whitespace-nowrap',
                      col.align === 'right' && 'justify-end',
                      col.align === 'center' && 'justify-center'
                    )}>
                      {col.label}
                      {col.sortable && (
                        sortField === col.key ? (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )
                        ) : (
                          <ChevronUp className="w-3 h-3 opacity-0" />
                        )
                      )}
                    </span>
                  </TableHead>
                ))}
                {/* Actions column */}
                <TableHead className="text-center w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayouts.map((payout) => {
                const finalizedDate = getFinalizedDate(payout);
                return (
                  <TableRow key={payout.id}>
                    <TableCell className="font-medium">{payout.loan_number}</TableCell>
                    <TableCell>{payout.borrower_name || '-'}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(payout.loan_amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(payout.gross_check_received)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(payout.gross_amount)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {payout.fund_date
                        ? parseAsLocalDate(payout.fund_date).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {finalizedDate
                        ? new Date(finalizedDate).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {payout.status === 'paid' ? (
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                          Paid
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                          Approved
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => onViewPayout(payout)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
